import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { type WorkspaceLayoutPreset } from 'generated/graphql';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { type WithApolloContext } from 'modules/Apollo/types';
import { activeWorkspaceLayoutPresetState, workspaceDisplayArrangementState } from './state';
import {
  buildDisplayArrangementKeyFromDisplaySlots,
  CompatibilityResult,
  getWorkspaceLayoutPresetCompatibility,
} from './utils';

type PresetToApplyReason = 'pick' | 'default' | 'pick-still-fits';

export type LayoutPresetForApply = Pick<
  WorkspaceLayoutPreset,
  'id' | 'name' | 'windows' | 'displayArrangementKey' | 'displaySlots' | 'isDefault' | 'updatedAt'
>;

// The layout in effect: the user's pick on the monitor setup it was picked on, else this setup's
// default, else the pick if it still fits. Only picking another or deleting it changes the pick.
export function useWorkspaceLayoutPresetToApply({ context }: WithApolloContext) {
  const { data } = useCurrentUser({ context });
  const displayArrangement = useRecoilValue(workspaceDisplayArrangementState);
  const activeLayoutPreset = useRecoilValue(activeWorkspaceLayoutPresetState);

  const currentDisplays = useMemo(() => displayArrangement?.displays ?? [], [displayArrangement]);
  const presets = useMemo<LayoutPresetForApply[]>(
    () => [...(data?.me?.workspaceSettings?.layoutSettings?.presets ?? [])],
    [data?.me?.workspaceSettings?.layoutSettings?.presets]
  );

  const exactDefaultPreset = useMemo(() => {
    return presets
      .filter(
        (preset) =>
          preset.isDefault &&
          getWorkspaceLayoutPresetCompatibility([...preset.displaySlots], currentDisplays) ===
            CompatibilityResult.Exact
      )
      .sort(byMostRecent)[0];
  }, [currentDisplays, presets]);

  const activePreset = useMemo(
    () => presets.find((preset) => preset.id === activeLayoutPreset?.presetId),
    [activeLayoutPreset?.presetId, presets]
  );

  const activePresetCompatibility = useMemo(() => {
    if (activePreset == null) return CompatibilityResult.Incompatible;

    return getWorkspaceLayoutPresetCompatibility([...activePreset.displaySlots], currentDisplays);
  }, [activePreset, currentDisplays]);

  // Selected in another window but not in this window's list yet (or deleted elsewhere)
  const isActivePresetMissing = activeLayoutPreset != null && activePreset == null;

  const currentArrangementKey = useMemo(
    () => buildDisplayArrangementKeyFromDisplaySlots(currentDisplays),
    [currentDisplays]
  );
  const isOnPickedSetup = activeLayoutPreset?.displayArrangementKey === currentArrangementKey;

  const { preset: presetToApply, reason: presetToApplyReason } = useMemo((): {
    preset?: LayoutPresetForApply;
    reason?: PresetToApplyReason;
  } => {
    // Wait for a fresh list instead of falling back; the owner clears it if it's really gone
    if (isActivePresetMissing) return {};

    const doesActivePresetFit =
      activePreset != null && activePresetCompatibility !== CompatibilityResult.Incompatible;
    // A new monitor setup brings its own default, e.g. opening the laptop lid
    if (doesActivePresetFit && isOnPickedSetup) return { preset: activePreset, reason: 'pick' };
    if (exactDefaultPreset != null) return { preset: exactDefaultPreset, reason: 'default' };

    return doesActivePresetFit ? { preset: activePreset, reason: 'pick-still-fits' } : {};
  }, [
    activePreset,
    activePresetCompatibility,
    exactDefaultPreset,
    isActivePresetMissing,
    isOnPickedSetup,
  ]);

  return {
    presetToApply,
    presetToApplyReason,
    currentArrangementKey,
    pickedOnArrangementKey: activeLayoutPreset?.displayArrangementKey,
    isActivePresetMissing,
  };
}

const byMostRecent = (a: LayoutPresetForApply, b: LayoutPresetForApply): number =>
  new Date(String(b.updatedAt)).getTime() - new Date(String(a.updatedAt)).getTime();
